/**
 * AnimateOnScroll Component
 * 
 * Scroll-triggered animations
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect, useRef, ReactNode } from 'react'
import './AnimateOnScroll.css'

type AnimationType = 
  | 'fadeIn' 
  | 'fadeInUp' 
  | 'fadeInDown' 
  | 'fadeInLeft' 
  | 'fadeInRight'
  | 'slideInUp'
  | 'slideInDown'
  | 'zoomIn'
  | 'bounceIn'
  | 'flipIn'

interface AnimateOnScrollProps {
  children: ReactNode
  animation?: AnimationType
  delay?: number
  duration?: number
  threshold?: number
  once?: boolean
  className?: string
}

const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  animation = 'fadeInUp',
  delay = 0,
  duration = 600,
  threshold = 0.2,
  once = true,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          if (once) {
            observer.unobserve(element)
          }
        } else if (!once) {
          setIsVisible(false)
        }
      },
      { threshold }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold, once])

  return (
    <div
      ref={ref}
      className={`animate-on-scroll ${animation} ${isVisible ? 'visible' : ''} ${className}`}
      style={{
        '--animation-delay': `${delay}ms`,
        '--animation-duration': `${duration}ms`
      }}
    >
      {children}
    </div>
  )
}

// Staggered animation wrapper for lists
interface StaggeredListProps {
  children: ReactNode
  staggerDelay?: number
  animation?: AnimationType
  className?: string
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  staggerDelay = 100,
  animation = 'fadeInUp',
  className = ''
}) => {
  return (
    <div className={`staggered-list ${className}`}>
      {React.Children.map(children, (child, index) => (
        <AnimateOnScroll 
          key={index} 
          animation={animation}
          delay={index * staggerDelay}
        >
          {child}
        </AnimateOnScroll>
      ))}
    </div>
  )
}

// Scroll reveal container
interface ScrollRevealProps {
  children: ReactNode
  className?: string
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({ children, className = '' }) => {
  return (
    <div className={`scroll-reveal ${className}`}>
      {children}
    </div>
  )
}

export default AnimateOnScroll